// PPS CRM - Documents Module
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface DocumentItem {
  id: string;
  documentType: string;
  s3Key: string;
  s3Bucket: string;
  createdAt: string;
  clientId: string;
  status?: string;
  client?: { fullLegalName: string };
}

export default function Documents() {
  const [docs, setDocs] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<any[]>([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    clientId: '',
    documentType: 'INVOICE',
    file: null as File | null,
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchDocs();
    fetchClients();
  }, []);

  const fetchDocs = async () => {
    try {
      const response = await api.get('/api/documents');
      setDocs(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/api/clients');
      setClients(response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleDownload = async (id: string) => {
    try {
      const response = await api.get(`/api/documents/${id}/download`);
      if (response.data.local) {
        const fileResponse = await api.get(`/api/documents/${id}/file`, { responseType: 'blob' });
        const blob = new Blob([fileResponse.data]);
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        return;
      }
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.clientId || !uploadForm.file) return;
    setUploading(true);
    try {
      const file = uploadForm.file;
      const reader = new FileReader();
      const fileData = await new Promise<string>((resolve, reject) => {
        reader.onerror = () => reject(new Error('File read error'));
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.readAsDataURL(file);
      });

      await api.post('/api/documents/upload', {
        clientId: uploadForm.clientId,
        documentType: uploadForm.documentType,
        fileName: file.name,
        fileData,
      });
      setShowUpload(false);
      setUploadForm({ clientId: '', documentType: 'INVOICE', file: null });
      fetchDocs();
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setUploading(false);
    }
  };

  const updateDocumentStatus = async (id: string, action: 'approve' | 'reject') => {
    try {
      await api.patch(`/api/documents/${id}/${action}`);
      fetchDocs();
    } catch (error) {
      console.error('Error updating document status:', error);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Documents</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowUpload(true)}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
          >
            Upload Document
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : docs.length === 0 ? (
          <div className="p-8 text-center text-dark-gray">No documents found</div>
        ) : (
          <table className="w-full">
            <thead className="bg-light-gray">
              <tr>
                <th className="px-4 py-3 text-left">Client</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((doc) => (
                <tr key={doc.id} className="border-b border-light-gray hover:bg-light-gray">
                  <td className="px-4 py-3">{doc.client?.fullLegalName || '-'}</td>
                  <td className="px-4 py-3">{doc.documentType}</td>
                  <td className="px-4 py-3">{doc.status || 'PENDING'}</td>
                  <td className="px-4 py-3">{new Date(doc.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleDownload(doc.id)}
                      className="text-primary hover:underline text-sm"
                    >
                      Download
                    </button>
                    {(doc.status || 'PENDING') === 'PENDING' && (
                      <>
                        <button
                          onClick={() => updateDocumentStatus(doc.id, 'approve')}
                          className="text-success hover:underline text-sm ml-3"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => updateDocumentStatus(doc.id, 'reject')}
                          className="text-primary hover:underline text-sm ml-3"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-xl font-bold mb-4">Upload Document</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Client</label>
                <select
                  value={uploadForm.clientId}
                  onChange={(e) => setUploadForm({ ...uploadForm, clientId: e.target.value })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                >
                  <option value="">Select client</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.fullLegalName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Document Type</label>
                <select
                  value={uploadForm.documentType}
                  onChange={(e) => setUploadForm({ ...uploadForm, documentType: e.target.value })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                >
                  <option value="INVOICE">Invoice</option>
                  <option value="PAY_STUB">Pay Stub</option>
                  <option value="RECEIPT">Receipt</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">File</label>
                <input
                  type="file"
                  onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowUpload(false)}
                className="px-4 py-2 border border-mid-gray rounded hover:bg-light-gray"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !uploadForm.clientId || !uploadForm.file}
                className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
